<template>
  <tr v-if="linkSet && linkSet.length && hasNonDefaultLinks">
    <th style="border-bottom: 0">
      <slot></slot>
    </th>
    <td style="border-bottom: 0">
      <div v-for="(linkType, index) in allGroupedLinksExcludingDefault" :key="index">
        <span style="font-weight: bold;">{{ getLinksetTitle(linkType.type) }}</span>
        <ul>
          <li v-for="(link, linkIndex) in linkType.links" :key="linkIndex">
            <a :href="link.href" target="_blank" rel="noopener">{{ link.title }}</a>
            <span v-if="link.hreflang && link.hreflang.length > 0" style="margin-left: 5px; font-size: 0.8em; color: #888;">
              [<span v-for="(lang, langIndex) in link.hreflang" :key="langIndex">
                <lang-string :lang="lang"></lang-string><span v-if="langIndex < link.hreflang.length - 1">, </span>
              </span>]
            </span>
          </li>
        </ul>
        <div style="margin-bottom: 10px"></div>
      </div>
    </td>
  </tr>
</template>

<script>
import { saveGet } from '@/mixins/saveGet'
import LangString from '@/components/LangString'

export default {
  name: 'LinksTr',
  props: ['linkSet'],
  components: {
    LangString
  },
  mixins: [saveGet],
  computed: {
    hasNonDefaultLinks() {
      if (!this.linkSet || !Array.isArray(this.linkSet)) {
        return false
      }

      const defaultLinkType = 'https://ref.gs1.org/voc/defaultLink'

      for (const linksetItem of this.linkSet) {
        if (linksetItem.links && Array.isArray(linksetItem.links)) {
          for (const link of linksetItem.links) {
            if (link['@linkType'] !== defaultLinkType) {
              return true
            }
          }
        }
      }

      return false
    },
    allGroupedLinksExcludingDefault() {
      if (!this.linkSet || !Array.isArray(this.linkSet)) {
        return []
      }

      const grouped = {}
      const defaultLinkType = 'https://ref.gs1.org/voc/defaultLink'

      // Collect all links from all linkset items
      for (const linksetItem of this.linkSet) {
        if (linksetItem.links && Array.isArray(linksetItem.links)) {
          for (const link of linksetItem.links) {
            const linkType = link['@linkType'] || 'defaultLink'

            // Skip default links
            if (linkType === defaultLinkType) {
              continue
            }

            if (!grouped[linkType]) {
              grouped[linkType] = []
            }

            // Avoid duplicates by checking href
            const exists = grouped[linkType].some(existingLink => existingLink.href === link.href)
            if (!exists) {
              grouped[linkType].push(link)
            }
          }
        }
      }

      return Object.entries(grouped).map(([type, links]) => ({
        type,
        links
      }))
    }
  },
  data() {
    return {
      linkTypes: [
        {
          title: 'Activity Ideas',
          value: 'https://www.gs1.org/voc/activityIdeas'
        },
        {
          title: 'Allergen Information',
          value: 'https://www.gs1.org/voc/allergenInfo'
        },
        {
          title: 'Careers Information',
          value: 'https://www.gs1.org/voc/careersInfo'
        },
        {
          title: 'Certification Information',
          value: 'https://www.gs1.org/voc/certificationInfo'
        },
        {
          title: 'Consumer Handling and Storage Information',
          value: 'https://www.gs1.org/voc/consumerHandlingStorageInfo'
        },
        {
          title: 'EPC Information Services (EPCIS) repository',
          value: 'https://www.gs1.org/voc/epcis'
        },
        {
          title: 'Events Information',
          value: 'https://www.gs1.org/voc/eventsInfo'
        },
        {
          title: 'Frequently Asked Questions',
          value: 'https://www.gs1.org/voc/faqs'
        },
        {
          title: 'Has Retailers',
          value: 'https://www.gs1.org/voc/hasRetailers'
        },
        {
          title: 'Homepage',
          value: 'https://www.gs1.org/voc/homepage'
        },
        {
          title: 'Ingredients Information',
          value: 'https://www.gs1.org/voc/ingredientsInfo'
        },
        {
          title: 'Instructions',
          value: 'https://www.gs1.org/voc/instructions'
        },
        {
          title: 'JSON Web Signature',
          value: 'https://www.gs1.org/voc/jws'
        },
        {
          title: 'Leave a Review',
          value: 'https://www.gs1.org/voc/leaveReview'
        },
        {
          title: 'Location Information',
          value: 'https://www.gs1.org/voc/locationInfo'
        },
        {
          title: 'Logistics Information',
          value: 'https://www.gs1.org/voc/logisticsInfo'
        },
        {
          title: 'Master Data',
          value: 'https://www.gs1.org/voc/masterData'
        },
        {
          title: 'Menu Information',
          value: 'https://www.gs1.org/voc/menuInfo'
        },
        {
          title: 'Nutritional Information',
          value: 'https://www.gs1.org/voc/nutritionalInfo'
        },
        {
          title: 'Opening Hours Information',
          value: 'https://www.gs1.org/voc/openingHoursInfo'
        },
        {
          title: 'Make Payment',
          value: 'https://www.gs1.org/voc/paymentLink'
        },
        {
          title: 'Product Information Page',
          value: 'https://www.gs1.org/voc/pip'
        },
        {
          title: 'Sustainability and Recycling (Deprecated)',
          value: 'https://www.gs1.org/voc/productSustainabilityInfo'
        },
        {
          title: 'Promotion',
          value: 'https://www.gs1.org/voc/promotion'
        },
        {
          title: 'Purchase Supplies or Accessories',
          value: 'https://www.gs1.org/voc/purchaseSuppliesOrAccessories'
        },
        {
          title: 'Quick Start Guide',
          value: 'https://www.gs1.org/voc/quickStartGuide'
        },
        {
          title: 'Recall Status',
          value: 'https://www.gs1.org/voc/recallStatus'
        },
        {
          title: 'Recipe Website',
          value: 'https://www.gs1.org/voc/recipeInfo'
        },
        {
          title: 'Register Purchase',
          value: 'https://www.gs1.org/voc/registerProduct'
        },
        {
          title: 'Entry in a Register',
          value: 'https://www.gs1.org/voc/registryEntry'
        },
        {
          title: 'Related Video',
          value: 'https://www.gs1.org/voc/relatedVideo'
        },
        {
          title: 'Reviews',
          value: 'https://www.gs1.org/voc/review'
        },
        {
          title: 'Safety Information',
          value: 'https://www.gs1.org/voc/safetyInfo'
        },
        {
          title: 'Scheduling/Reservation',
          value: 'https://www.gs1.org/voc/scheduleTime'
        },
        {
          title: 'Service Information',
          value: 'https://www.gs1.org/voc/serviceInfo'
        },
        {
          title: 'Smart Label',
          value: 'https://www.gs1.org/voc/smartLabel'
        },
        {
          title: 'Smart Label',
          value: 'https://ref.gs1.org/voc/smartLabel'
        },
        {
          title: 'Default Link',
          value: 'https://ref.gs1.org/voc/defaultLink'
        },
        {
          title: 'Product Information Page',
          value: 'https://ref.gs1.org/voc/pip'
        },
        {
          title: 'Social Media',
          value: 'https://www.gs1.org/voc/socialMedia'
        },
        {
          title: 'Statistics',
          value: 'https://www.gs1.org/voc/statisticInfo'
        },
        {
          title: 'Support',
          value: 'https://www.gs1.org/voc/support'
        },
        {
          title: 'Sustainability and Recycling',
          value: 'https://www.gs1.org/voc/sustainabilityInfo'
        },
        {
          title: 'Traceability Information',
          value: 'https://www.gs1.org/voc/traceability'
        },
        {
          title: 'Tutorial',
          value: 'https://www.gs1.org/voc/tutorial'
        },
        {
          title: 'Sign waiver/agreement/document',
          value: 'https://www.gs1.org/voc/userAgreement'
        },
        {
          title: 'Verification Service',
          value: 'https://www.gs1.org/voc/verificationService'
        },
        {
          title: "What's in the box?",
          value: 'https://www.gs1.org/voc/whatsInTheBox'
        }
      ]
    }
  },
  methods: {
    getLinksetTitle(key) {
      if (!key) {
        return 'No Link Type'
      }
      const linkType = this.linkTypes.find(item => item.value === key)
      if (linkType) {
        return linkType.title
      }
      // If not found, try to extract a readable name from the URL
      const regex = /\/voc\/(.+)|gs1:(.+)/
      const match = key.match(regex)
      if (match) {
        const searchKey = match[1] || match[2]
        const linkTypeBySearch = this.linkTypes.find(item =>
          item.value.includes(searchKey)
        )
        if (linkTypeBySearch) {
          return linkTypeBySearch.title
        }
        // Convert camelCase to readable format
        return searchKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
      }
      return key
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/LinksTr.vue